/**
 * FixedPageEditor — shape: fixed_page (STAINLESS_STEEL_PLUMBING, HYBRID).
 * These families render a single fixed page: just the `spec` (title + KPI tiles +
 * spec rows). Any extra family data (plumbing's tubeMatrix / fittingFamilies,
 * HYBRID's empty `models`, the CONCEPT_ONLY source flag) round-trips untouched.
 *
 * HYBRID stays render-gated (`incomplete`) until its values are complete — the
 * resolver enforces that; here we just surface the gate as a banner.
 */

import { AlertTriangle } from 'lucide-react';
import { CategorySpecEditor, type CategorySpecShape } from './specPrimitives';

export function FixedPageEditor({
  data,
  tokens,
  familyKey,
  onChange,
}: {
  data: any;
  tokens: string[];
  familyKey: string;
  onChange: (next: any) => void;
}) {
  const spec: CategorySpecShape = data?.spec ?? { title: '', kpiTiles: [], specRows: [] };
  const setSpec = (next: CategorySpecShape) => onChange({ ...data, spec: next });

  const isHybrid = familyKey === 'HYBRID';
  // HYBRID is gated by its source flag / null values until Sun Stellar authors it.
  const conceptOnly =
    typeof data?.specSourceFlag === 'string' && data.specSourceFlag.includes('CONCEPT_ONLY');

  return (
    <>
      {isHybrid && conceptOnly && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            padding: '10px 12px',
            marginBottom: 16,
            borderRadius: 9,
            background: 'rgba(201,138,59,.10)',
            border: '1px solid var(--saffron-600)',
            color: 'var(--ink-1)',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          <AlertTriangle size={16} style={{ color: 'var(--saffron-600)', flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Render-gated.</strong> This family is concept-only — proposals will
            keep it hidden until every value is complete (no <code>{'{placeholder}'}</code>
            and no empty hero values). Editing here is allowed; it surfaces in proposals
            once the resolver considers it complete.
          </span>
        </div>
      )}

      <CategorySpecEditor spec={spec} tokens={tokens} onChange={setSpec} />
    </>
  );
}
